/* eslint-disable @typescript-eslint/no-unused-vars */
import type { ContentHref, PersonHref } from '@generalTypes/apiTypes';
import { selectContentItem } from '@newStore/documentApi/documentApiSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import { type EditComponent, RequiredType } from '@nodeTypeConfig/configTypes';
import type { BatchItem } from '@generalTypes/sriTypes';
import { isEqual } from 'lodash';
import { selectPersonReferenceNode } from '@newStore/documentUI/transformContent/referenceGroupSelectors';
import type { RootState } from '@generalTypes/rootStateTypes';
import { defaultContactsParameters } from '@store/constants/constants';
import { VALIDATION_PENDING } from '../validationHelpers';
import type { AsyncValidationRule, ValidationRuleName } from '../validationTypes';
import { createError } from '../createError';

const selectReferencePersonHash: AsyncValidationRule['selectInvalidateDataOnChangeOf'] = (
  state,
  href: ContentHref,
) => selectPersonReferenceNode(state, href)?.person?.href || '';

const selectPersonReferenceValidationData = (state: RootState, href: ContentHref) =>
  !state.validationData[href]?.referenceContactPersonActive?.isLoading &&
  selectReferencePersonHash(state, href) ===
    state.validationData[href]?.referenceContactPersonActive?.validityHash &&
  state.validationData[href]?.referenceContactPersonActive?.data;

const getSearchBatchItem = (hrefs: PersonHref[]) => {
  const searchParams = new URLSearchParams({
    ...defaultContactsParameters,
    limit: hrefs.length.toString(),
    hrefs: hrefs.join(','),
  });

  const batchItem: BatchItem<'GET'> = {
    verb: 'GET',
    href: `/search?${searchParams}`,
  };
  return batchItem;
};

const selectReferenceContactPersonValidation: AsyncValidationRule['selectValidation'] =
  createTypedSelector(
    [
      (state, href: ContentHref) => selectContentItem(state, href),
      (state, href: ContentHref) => selectPersonReferenceValidationData(state, href),
    ],
    (content, validationData) => {
      if (!content || !validationData) {
        return VALIDATION_PENDING;
      }
      const hasResponsibilities = validationData.some((item) => item.responsibilities?.length > 0);
      if (hasResponsibilities) {
        return true;
      }
      return createError(
        'contactPersonInactive',
        'isContactCardPersonActive',
        'De contactpersoon heeft geen actieve verantwoordelijkheid binnen KathOndVla',
        { component: 'contactCard' },
        RequiredType.WARNING,
      );
    },
    {
      memoizeOptions: {
        resultEqualityCheck: isEqual,
      },
    },
  );

export const isReferenceContactPersonActive: AsyncValidationRule = {
  getName: (): ValidationRuleName => 'referenceContactPersonActive',
  isAsync: true,
  selectNeedsData: (state, href) => {
    return selectPersonReferenceValidationData(state, href) === undefined;
  },
  selectDataBatch: createTypedSelector(
    [selectPersonReferenceNode],
    (personReference) => {
      if (!personReference?.person) {
        return null;
      }
      return getSearchBatchItem([personReference.person.href]);
    },
    {
      memoizeOptions: {
        resultEqualityCheck: isEqual,
      },
    },
  ),
  selectValidation: selectReferenceContactPersonValidation,
};

const selectContactPersonHash: AsyncValidationRule['selectInvalidateDataOnChangeOf'] = (
  state,
  href: ContentHref,
) => selectContentItem(state, href)?.contacts?.toString() || '';

const selectContactsValidationData = (state: RootState, href: ContentHref) =>
  !state.validationData[href]?.contactPersonActive?.isLoading &&
  selectContactPersonHash(state, href) ===
    state.validationData[href]?.contactPersonActive?.validityHash &&
  state.validationData[href]?.contactPersonActive?.data;

const selectContactPersonsValidation: AsyncValidationRule['selectValidation'] = createTypedSelector(
  [
    (state, href: ContentHref) => selectContentItem(state, href),
    (state, href: ContentHref) => selectContactsValidationData(state, href),
    (
      state,
      href: ContentHref,
      parent: ContentHref | undefined,
      config: EditComponent | undefined,
    ) => config,
  ],
  (content, validationData, config) => {
    if (!content || !validationData) {
      return VALIDATION_PENDING;
    }
    if (!content.contacts?.length) {
      return true;
    }
    for (const contactHref of content.contacts) {
      const contactIsOnKOV = validationData.find((item) => item.$$meta.permalink === contactHref)
        ?.responsibilities?.length;
      if (!contactIsOnKOV) {
        return createError(
          'contactPersonInactive',
          'areContactPersonsActive',
          'Er zijn contactpersonen zonder actieve verantwoordelijkheid binnen KathOndVla',
          { component: 'contacts', property: 'contacts' },
          RequiredType.WARNING,
        );
      }
    }
    return true;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const areContactPersonsActive: AsyncValidationRule = {
  getName: (): ValidationRuleName => 'contactPersonActive',
  isAsync: true,
  selectNeedsData: (state, href) => {
    return selectContactsValidationData(state, href) === undefined;
  },
  selectInvalidateDataOnChangeOf: selectContactPersonHash,
  selectDataBatch: createTypedSelector(
    [(state, href) => selectContentItem(state, href)],
    (content) => {
      if (!content.contacts?.length) {
        return null;
      }
      return getSearchBatchItem(content.contacts);
    },
    {
      memoizeOptions: {
        resultEqualityCheck: isEqual,
      },
    },
  ),
  selectValidation: selectContactPersonsValidation,
};
